import i18n from '../langs'
import type { Tag, TagTypeValue } from '@/types/services/tags'

export const TagType = {
  CLASSIFICATION: 'classification',
  TAG: 'tag',
  TYPE: 'type',
  CATEGORY_TAG: 'category_tag',
  SEGMENT_TAG: 'segment_tag',
  ALLOCATION_TAG: 'allocation_tag',
  OPEN_POSITION_TAG: 'open_position_tag',
} as const

/**
 * Tag types the backend can merge (`account_tag.merge_tags`).
 *
 * Everything else -- notably segment and allocation tags -- is rejected there:
 * `asset_info_tag` has no FK to `root.tags`, so a merge that fell through
 * would orphan every link once the caller deleted the source tag.
 */
export const MERGEABLE_TAG_TYPES: TagTypeValue[] = [
  TagType.TAG,
  TagType.CATEGORY_TAG,
  TagType.OPEN_POSITION_TAG,
]

/**
 * Tag types whose `note` reaches the AI classifier as a `label_notes` line
 * (`prompt.build_user_prompt`). The note is only offered for editing on these
 * -- on the rest it would be a field nothing reads.
 */
export const AI_NOTE_TAG_TYPES: TagTypeValue[] = [
  TagType.ALLOCATION_TAG,
  TagType.SEGMENT_TAG,
  TagType.CATEGORY_TAG,
]

export const NO_CATEGORY_TAG_ID = '_no_category_'

export const NO_CATEGORY_TAG: Tag = {
  id: NO_CATEGORY_TAG_ID,
  name: i18n.global.t('ui.common.other'),
  entity_id: '',
  type: 'category_tag',
}

export const NO_SEGMENT_TAG_ID = '_no_segment_'

export const NO_SEGMENT_TAG: Tag = {
  id: NO_SEGMENT_TAG_ID,
  name: i18n.global.t('ui.common.other'),
  entity_id: '',
  type: 'segment_tag',
}

// Assets whose currency had no segment carry no allocation tag at all, so the
// "unallocated" bucket is client-side only. Every real allocation -- including
// NFT -- is a per-entity tag with a UUID id.
export const NO_ALLOCATION_TAG_ID = '_no_allocation_'

export const NO_ALLOCATION_TAG: Tag = {
  id: NO_ALLOCATION_TAG_ID,
  name: i18n.global.t('ui.common.other'),
  entity_id: '',
  type: 'allocation_tag',
}

// The client-side 'Other' buckets, one per axis.
export const OTHER_BUCKET_TAG_IDS = new Set<string>([
  NO_CATEGORY_TAG_ID,
  NO_SEGMENT_TAG_ID,
  NO_ALLOCATION_TAG_ID,
])

// The AI contract passes allocations by vocabulary *name*, not by id, and uses
// this literal for "unallocated" -- it must not follow the id above.
export const UNALLOCATED_ALLOCATION_NAME = 'Other'
